'use client'

import { createContext, useContext, useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import { AppHeader, type AppHeaderConfig } from '@/components/AppHeader'
import { ProjectNavigationOverlay } from '@/components/ProjectNavigationOverlay'
import { WorkspaceSwitchOverlay } from '@/components/WorkspaceSwitchOverlay'

type SetHeaderConfig = (config: AppHeaderConfig | null) => void

const SetAppHeaderContext = createContext<SetHeaderConfig | null>(null)

// useLayoutEffect on the server warns; fall back to useEffect there. The header
// config is client-only behavior, so this is purely about silencing SSR noise.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * Renders the one persistent {@link AppHeader} above all authenticated content.
 *
 * Routes opt in by calling {@link useAppHeader}; routes that don't (the editor,
 * backstage, the onboarding flows that draw their own over-video bar) leave the
 * shared header unrendered. Because the header lives here in the layout — above
 * each page's own loading/error gates — it stays mounted across navigation rather
 * than unmounting and flashing on every route change.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppHeaderConfig | null>(null)
  return (
    <SetAppHeaderContext.Provider value={setConfig}>
      {config && <AppHeader {...config} />}
      <ProjectNavigationOverlay />
      <WorkspaceSwitchOverlay />
      {children}
    </SetAppHeaderContext.Provider>
  )
}

/**
 * Declare the shared header's contents for the current route. Call once near the
 * top of a page or layout, BEFORE any early `return` (Rules of Hooks), so the
 * header is configured even while the page renders its own loading state. The
 * config resets when the route unmounts, so sibling routes that don't opt in
 * render no header. Pass `null` to keep it hidden.
 */
export function useAppHeader(config: AppHeaderConfig | null) {
  const setConfig = useContext(SetAppHeaderContext)
  // The config is plain serializable data; key the effect on its JSON so it only
  // re-runs when the contents actually change, not on every render.
  const key = config ? JSON.stringify(config) : ''
  // `key` is the JSON of `config`, so the effect re-runs whenever the contents
  // change without depending on the object's identity (which changes each render).
  useIsomorphicLayoutEffect(() => {
    setConfig?.(config)
    return () => setConfig?.(null)
  }, [setConfig, key])
}
